import { FC, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import { ChevronDownIcon } from '../../../../assets/icons/index';
import { Tooltip } from '../../../../components/tooltip/index';
import { useLocale } from '../../../../hooks/index';
import {
  Column,
  ColumnVariant,
  DetailsSortVariant,
  detailsViewByColumns,
  ShareClassColumns,
  StakeholderColumns,
  ViewBy,
} from '../../../../types/snapshot.types';
import {
  capTableExpandedSurfaceTransition,
  capTableStickyExpandClass,
  capTableStickyNameClass,
  getCapTableViewByMode,
} from './cap-table-sticky-classes';

type StakeholderTableBodyProps = {
  className?: string;
  disableExpand?: boolean;
  handleExpandRow?: React.Dispatch<React.SetStateAction<boolean>>;
  isExpanded?: boolean;
  isExpandedRow: boolean;
  isExpandedVisual?: boolean;
  isLastInExpandedGroup?: boolean;
  tdClassName?: string;
  trClassName?: string;
  viewBy: Record<ViewBy, boolean>;
  visibleColumns: Column[];
} & (
  | {
      fields: Record<ShareClassColumns, null | number | number[] | string | string[]>;
      type: DetailsSortVariant.BY_SHARE_CLASS;
    }
  | {
      fields: Record<StakeholderColumns, null | number | number[] | string | string[]>;
      type: DetailsSortVariant.BY_STAKEHOLDER;
    }
);
const allColumnsMap = Object.values(Column);

const allColumnFields = allColumnsMap.reduce(
  (acc, curr) => ({ ...acc, [curr]: null }),
  {},
) as Record<Column, null>;

const resultZero = '0.00';

export const TableRow: FC<StakeholderTableBodyProps> = ({
  fields,
  className,
  trClassName,
  visibleColumns = [],
  viewBy,
  handleExpandRow,
  isExpanded,
  isExpandedVisual,
  isExpandedRow,
  isLastInExpandedGroup = false,
  type,
  tdClassName,
  disableExpand = false,
}) => {
  const selectedViewBy = getCapTableViewByMode(viewBy);

  const { messagesLocale } = useLocale();
  const viewByAll = selectedViewBy === 'all';
  const expandedVisual = isExpandedVisual ?? isExpanded;
  const isInExpandedGroup = Boolean(expandedVisual) || isExpandedRow;
  const isFirstInExpandedGroup = isInExpandedGroup && !isExpandedRow;

  const basicIsVisibleChecks = useMemo(
    () =>
      allColumnsMap.reduce(
        (acc, curr) => ({ ...acc, [curr]: visibleColumns.includes(curr) }),
        {},
      ) as Record<Column, boolean>,
    [visibleColumns],
  );

  const isVisibleChecks: Record<ColumnVariant, boolean> = useMemo(
    () => ({
      ...basicIsVisibleChecks,
      [Column.NAME]: visibleColumns.includes(Column.NAME) && viewByAll,
      [Column.SHARE_CLASS_NAME]:
        visibleColumns.includes(Column.SHARE_CLASS_NAME) && !viewByAll && viewBy.type,
      [Column.STAKEHOLDER_NAME]:
        visibleColumns.includes(Column.STAKEHOLDER_NAME) && !viewByAll && viewBy.stakeholders,
      [Column.TYPE]: visibleColumns.includes(Column.TYPE) && !viewByAll,
    }),
    [basicIsVisibleChecks, viewBy.stakeholders, viewBy.type, viewByAll, visibleColumns],
  );

  const fieldsMap = useMemo(
    () => Object.values(detailsViewByColumns[selectedViewBy][type]),
    [selectedViewBy, type],
  );
  const firstVisibleField = fieldsMap.find((field) => isVisibleChecks[field]);

  const allFields = useMemo(() => ({ ...allColumnFields, ...fields }), [fields]);
  const cellDividerClass =
    !isInExpandedGroup || isLastInExpandedGroup ? 'shadow-[inset_0px_-1px_0px_0px_#F2F4F7]' : '';
  const showExpandControl = !disableExpand && !isExpandedRow;
  const expandedAccentClass = isInExpandedGroup
    ? twMerge(
        'pointer-events-none absolute left-0 w-0.5 bg-brand-700',
        isFirstInExpandedGroup ? 'top-0' : '-top-px',
        isLastInExpandedGroup ? 'bottom-0' : '-bottom-px',
      )
    : '';
  /**
   * Only the frozen columns carry a background of their own — they scroll over the other cells and
   * would be see-through without one, and `capTableStickyNameClass` already supplies it.
   *
   * The rest are left transparent so the surface behind them shows through: the row's own
   * `bg-brand-25` for the row being expanded, and the nested table's for the rows inside it. That
   * is one fading element per row instead of one per cell, and it is what keeps open and close
   * symmetrical — a per-cell background with no transition snapped on the way in and only looked
   * right on the way out, where it fell back to transparent and revealed the row fading underneath.
   */
  const dataCellClass = (isFrozenName: boolean) =>
    twMerge(
      'px-4 py-6',
      isFrozenName &&
        capTableStickyNameClass({
          hasExpandColumn: viewByAll,
          isExpanded: isInExpandedGroup,
          isHeader: false,
        }),
      cellDividerClass,
    );

  return (
    <tr
      className={twMerge(
        isInExpandedGroup && twMerge('bg-brand-25', capTableExpandedSurfaceTransition),
        trClassName,
      )}
    >
      {viewByAll && (
        <td
          className={twMerge(
            'relative box-border p-0 align-middle',
            capTableStickyExpandClass({ isHeader: false, isExpanded: isInExpandedGroup }),
            cellDividerClass,
            showExpandControl && 'cursor-pointer',
            className,
          )}
          onClick={() => showExpandControl && handleExpandRow?.((prev) => !prev)}
        >
          {isInExpandedGroup && <span aria-hidden className={expandedAccentClass} />}
          <span className="centered flex w-6 shrink-0 py-6 pl-4 text-sm font-[450] text-gray-700">
            {showExpandControl && (
              <ChevronDownIcon
                className={twMerge(
                  'transition-transform duration-300 ease-in-out',
                  isExpanded && 'rotate-180',
                )}
                iconColor={isExpanded ? '#2565C8' : '#344054'}
              />
            )}
          </span>
        </td>
      )}

      {fieldsMap.map(
        (field, i) =>
          isVisibleChecks[field] &&
          (Array.isArray(allFields[field]) ? (
            allFields[field].map((filed, cellIndex) => (
              <td
                className={dataCellClass(field === firstVisibleField && cellIndex === 0)}
                key={`${field}_${cellIndex}_${type}`}
              >
                <span
                  className={twMerge(
                    'text-sm font-[450] text-gray-700',
                    (!allFields[field] || allFields[field] === '-' || !filed) && 'text-gray-300',
                    tdClassName,
                  )}
                >
                  {filed ? filed.toLocaleString('en-US') : '-'}
                </span>
              </td>
            ))
          ) : (
            <td
              className={dataCellClass(field === firstVisibleField)}
              key={`${field}_${i}_${type}`}
            >
              <span
                className={twMerge(
                  'block max-w-[360px] overflow-hidden truncate text-ellipsis whitespace-nowrap text-sm font-[450] text-gray-700',
                  allFields[field] === '-' && 'text-gray-300',
                  tdClassName,
                )}
              >
                {!allFields[field] || allFields[field] === resultZero ? (
                  0
                ) : field === Column.STAKEHOLDER_NAME ||
                  (field === Column.NAME &&
                    isExpandedRow &&
                    allFields[field] !== messagesLocale['TOTAL']) ? (
                  <Tooltip childrenWrapperClassName="max-w-[360px]" content={allFields[field]}>
                    <span className="truncate">{allFields[field]}</span>
                  </Tooltip>
                ) : (
                  allFields[field]?.toLocaleString('en-US')
                )}
              </span>
            </td>
          )),
      )}
    </tr>
  );
};
